import { useEffect, useState, type ReactNode } from 'react';
import { Button, Link, Stack, Text } from '@chakra-ui/react';
import type { Bridge } from '@casa/bridge';
import { isCloudKind, isServerPage, readServerInfo } from '../data/casa_server.ts';
import { CardPage } from '../ui/chrome_page.tsx';
import { followRoute, routeHref } from '../ui/route.ts';

// tilez-cloud does not show dashboards in a browser (docs/specs/secrets.md SE-18, QSE-8, the owner
// 2026-11-04): a page it served could be made to read the credentials' keys, so dashboards are used
// from the apps, or in a browser through the user's own tilez-hub. Its admin page stays (/admin),
// and, where it has accounts, their pages (/account; cloud-accounts.md).

export interface CloudPageInfo {
    /** Undefined while the server is asked. A page from a shell, from another kind of server, or from one that does not answer, is not. */
    isCloud: boolean | undefined;
    /** Whether it has accounts (`signIn` lists `account`). */
    hasAccounts: boolean;
}

/** Whether this page came from a tilez-cloud, and whether it has accounts. */
export function useCloudPage(bridge: Bridge | null) : CloudPageInfo {
    const [info, setInfo] = useState<CloudPageInfo>({ isCloud: isServerPage() && bridge ? undefined : false, hasAccounts: false });

    useEffect(() => {
        if (!isServerPage() || !bridge) {
            return;
        }

        readServerInfo(bridge, { kind: 'page' }).then(
            (server) => setInfo({ isCloud: isCloudKind(server?.kind), hasAccounts: server?.signIn.includes('account') ?? false }),
            () => setInfo({ isCloud: false, hasAccounts: false }),
        );
    }, [bridge]);

    return info;
}

/** What a browser on tilez-cloud shows instead of a dashboard (cloud-accounts AC-24's card). */
export function CloudPage({ hasAccounts }: { hasAccounts: boolean }) : ReactNode {
    return (
        <CardPage title="Tilez" note={`Tilez Cloud · ${location.host}`}>
            <Text textAlign="center">Tilez Cloud keeps your dashboards for the Tilez app. It does not show them in a browser, so that the credentials in them stay encrypted on your devices.</Text>
            <Text color="fg.muted" textAlign="center">
                Open the Tilez app on your tablet or phone. To use a dashboard in a browser, use a Tilez Hub in your own home.
            </Text>
            {hasAccounts ? (
                <Stack gap="2">
                    <Button asChild colorPalette="blue">
                        <a href={routeHref('/account')} onClick={(event) => followRoute(event, '/account')}>Sign in</a>
                    </Button>
                    <Button asChild variant="outline">
                        <a href={routeHref('/account/signup')} onClick={(event) => followRoute(event, '/account/signup')}>Create an account</a>
                    </Button>
                </Stack>
            ) : null}
            <Link href="/pair?next=/admin" color="fg.muted" fontSize="sm" alignSelf="center">
                Admin page
            </Link>
        </CardPage>
    );
}
